import appleIcon from '@/assets/logos/apple.svg';
import googleIcon from '@/assets/logos/google-color.svg';
import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import { IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED } from '@/components/constants/flags';
import {
  KINDE_APPLE_CONNECTION_ID,
  KINDE_GMAIL_CONNECTION_ID,
} from '@/components/constants/kinde';
import { iframeSafeAuthRoute } from '@/components/modal/sign-in-modal/utils';
import Loader from '@/components/ui/loader';
import { signInClicked } from '@/lib/analytics/scribe/auth';
import { cn } from '@/lib/cn';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { appendAmplitudeCrossDomainParams } from '@/utils/amplitudeCrossDomain';
import { isAppleSignInAvailable } from '@/utils/appConfiguration';
import { appendAttributionParamsToRedirectUrl } from '@/utils/attributionParams';
import { isAuth0SocialLoginGoogleAllowed } from '@/utils/auth/isAuth0SocialLoginGoogleAllowed';
import { prefixWithLocale } from '@/utils/localeRedirect';
import { logger } from '@/utils/logger/logger';
import {
  AUTH0_APPLE_CONNECTION,
  AUTH0_GOOGLE_CONNECTION,
  AUTH0_LOGIN_PATH,
} from '@heidi/auth/contracts';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { Separator } from '@heidi/design-system/separator';
import {
  TypographyH2,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { ArrowLeft, Mail } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { bookDemoEmailStepMessages } from './book-demo-email-step.messages';

const EARLY_MEETING_STORAGE_KEY = 'earlyMeetingDemoReturn';

const GOOGLE_SOCIAL_PROVIDER = {
  id: 'google',
  connectionId: KINDE_GMAIL_CONNECTION_ID,
  icon: googleIcon,
  labelMessage: bookDemoEmailStepMessages.continueWithGoogle,
  logoAltMessage: bookDemoEmailStepMessages.googleLogoAlt,
} as const;

const APPLE_SOCIAL_PROVIDER = {
  id: 'apple',
  connectionId: KINDE_APPLE_CONNECTION_ID,
  icon: appleIcon,
  labelMessage: bookDemoEmailStepMessages.continueWithApple,
  logoAltMessage: bookDemoEmailStepMessages.appleLogoAlt,
} as const;

function getSocialProviders() {
  return [
    GOOGLE_SOCIAL_PROVIDER,
    ...(isAppleSignInAvailable() ? [APPLE_SOCIAL_PROVIDER] : []),
  ] as const;
}

interface BookDemoEmailStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function BookDemoEmailStep({
  email,
  onEmailChange,
  onContinue,
  onBack,
}: BookDemoEmailStepProps) {
  const intl = useIntl();
  const { locale } = intl;
  const { login } = useKindeAuth();
  const { login: desktopAuthLogin } = useDesktopAuthSafe();
  const shouldUseDesktopAuth = isTauri();

  const [clicked, setClicked] = useState<'email' | 'google' | 'apple'>();

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const socialRedirectUrl = appendAmplitudeCrossDomainParams(
    appendAttributionParamsToRedirectUrl(
      prefixWithLocale(locale, '/onboarding?flow=book-demo')
    )
  );

  const handleEmailContinue = () => {
    if (!isValidEmail) return;
    setClicked('email');
    onContinue();
  };

  const resetSocialLoginState = () => {
    sessionStorage.removeItem(EARLY_MEETING_STORAGE_KEY);
    setClicked(undefined);
  };

  const handleSocialLogin = (provider: 'google' | 'apple', kindeConnectionId: string) => {
    setClicked(provider);
    signInClicked(provider);

    // Store flag so we can resume at calendar step after auth
    sessionStorage.setItem(EARLY_MEETING_STORAGE_KEY, 'true');

    const isAuth0Social =
      provider === 'google'
        ? isAuth0SocialLoginGoogleAllowed()
        : IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED();

    if (isAuth0Social) {
      const auth0Connection =
        provider === 'google' ? AUTH0_GOOGLE_CONNECTION : AUTH0_APPLE_CONNECTION;

      if (shouldUseDesktopAuth) {
        void desktopAuthLogin({
          connection: auth0Connection,
          uiLocales: toAuth0UiLocales(locale),
        }).catch((error) => {
          resetSocialLoginState();
          logger.error(`[Auth0] Desktop ${provider} login failed`, error);
        });
        return;
      }

      const params = new URLSearchParams();
      params.set('returnTo', socialRedirectUrl);
      params.set('connection', auth0Connection);
      if (locale) params.set('ui_locales', toAuth0UiLocales(locale));
      iframeSafeAuthRoute(`${AUTH0_LOGIN_PATH}?${params.toString()}`);
      return;
    }

    const socialQuery = {
      post_login_redirect_url: socialRedirectUrl,
      connection_id: kindeConnectionId,
    };
    if (isUsingReactKinde()) {
      void login({ authUrlParams: { ...socialQuery, lang: locale } });
      return;
    }

    resetSocialLoginState();
    logger.error(`[Auth] Unsupported social login runtime for ${provider}`);
  };

  const isButtonDisabled = clicked !== undefined;
  const isEmailButtonDisabled = !email || !isValidEmail || isButtonDisabled;

  return (
    <div className="relative z-10 flex w-full flex-col items-center gap-10 px-6 py-8">
      {/* Header */}
      <div className="flex flex-col items-center gap-6">
        <Image
          src={IconHorizontalRebrand}
          alt={intl.formatMessage(bookDemoEmailStepMessages.brandIconAlt)}
          width={80}
          height={24}
          className="dark-invert"
        />
        <div className="flex flex-col items-center gap-1 text-center">
          <TypographyH2>
            <FormattedMessage {...bookDemoEmailStepMessages.title} />
          </TypographyH2>
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage {...bookDemoEmailStepMessages.subtitle} />
          </TypographyP2>
        </div>
      </div>

      {/* Email form */}
      <div className="flex w-full max-w-[400px] flex-col gap-4 rounded-2xl bg-surface p-6 shadow-lg">
        {/* Email Input */}
        <div className="flex flex-col gap-1">
          <TypographyP2Bold>
            <FormattedMessage {...bookDemoEmailStepMessages.workEmailLabel} />
          </TypographyP2Bold>
          <Input
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            id="demo-email"
            type="email"
            placeholder={intl.formatMessage(
              bookDemoEmailStepMessages.workEmailPlaceholder
            )}
            autoComplete="email"
            accessoryStart={<Mail size={16} className="text-text-disabled" />}
            data-testid="demo-email-input"
          />
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-2">
          {/* Divider */}
          <div className="flex w-full items-center gap-6">
            <Separator className="flex-1" />
            <TypographyP2 className="text-text-tertiary">
              <FormattedMessage {...bookDemoEmailStepMessages.orDivider} />
            </TypographyP2>
            <Separator className="flex-1" />
          </div>

          {/* Social Login Buttons */}
          {getSocialProviders().map(
            ({ id, connectionId, icon, labelMessage, logoAltMessage }) => (
              <Button
                key={id}
                variant="secondary"
                size="lg"
                disabled={isButtonDisabled}
                onClick={() => handleSocialLogin(id, connectionId)}
                disableAutomaticSvgSizing
              >
                {clicked === id ? (
                  <Loader className="size-4.5" />
                ) : (
                  <Image
                    src={icon}
                    alt={intl.formatMessage(logoAltMessage)}
                    className={cn('size-4.5', id === 'apple' && 'dark-invert')}
                  />
                )}
                <FormattedMessage {...labelMessage} />
              </Button>
            )
          )}

          <Button
            size="lg"
            disabled={isEmailButtonDisabled}
            onClick={handleEmailContinue}
            data-testid="demo-continue-button"
          >
            {clicked === 'email' ? (
              <Loader className="size-5 text-text-primary-invert" />
            ) : (
              <FormattedMessage {...bookDemoEmailStepMessages.continue} />
            )}
          </Button>
        </div>
      </div>

      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="gap-1.5 text-text-secondary"
      >
        <ArrowLeft size={16} className="rtl:-scale-x-100" />
        <FormattedMessage {...bookDemoEmailStepMessages.back} />
      </Button>
    </div>
  );
}

export { EARLY_MEETING_STORAGE_KEY };
